<template>
  <v-card>
    <v-toolbar color="secondary" class="elevation-0">
      <v-toolbar-title> Ledger </v-toolbar-title>
    </v-toolbar>
    <v-card-actions>
      <div>
        <v-menu :close-on-content-click="false" offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-bind="attrs"
              v-on="on"
              dense
              outlined
              :value="startDate"
              prepend-inner-icon="mdi-calendar-clock"
              append-inner-icon="mdi-menu-down"
              class="mx-5"
              style="height: 40px; min-width: 180px"
              label="From"
              hide-details
            />
          </template>
          <v-list class="pa-0" style="max-width: fit-content">
            <v-list-item class="pa-0">
              <v-list-item-title>
                <v-date-picker
                  v-model="startDate"
                  show-adjacent-months
                  :max="endDate"
                />
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
      <div>
        <v-menu :close-on-content-click="false" offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-bind="attrs"
              v-on="on"
              dense
              outlined
              :value="endDate"
              prepend-inner-icon="mdi-calendar-clock"
              append-inner-icon="mdi-menu-down"
              class="mx-5"
              style="height: 40px; min-width: 180px"
              label="To"
              hide-details
            />
          </template>
          <v-list class="pa-0" style="max-width: fit-content">
            <v-list-item class="pa-0">
              <v-list-item-title>
                <v-date-picker
                  v-model="endDate"
                  show-adjacent-months
                  :min="startDate"
                />
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
      <v-text-field
        v-model="search"
        append-icon="mdi-magnify"
        label="Search"
        single-line
        dense
        outlined
        hide-details
      ></v-text-field>

      <v-btn color="grey darken-1 ml-5" :loading="loading" @click="requestCSV">
        <v-icon class="mr-3">mdi-download</v-icon>
        Export as CSV
      </v-btn>
    </v-card-actions>
    <v-card-text>
      <v-data-table
        :headers="headers"
        :items="items"
        dense
        :items-per-page="15"
        :search="search"
      >
        <template v-slot:[`item.created_at`]="{ item }">
          {{ item.created_at.toLocaleDateString() }}
        </template>
        <template v-slot:[`item.details`]="{ item }">
          <v-btn icon @click="showDetails(item)">
            <v-icon>mdi-clipboard-outline</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card-text>

    <v-dialog v-model="detailsDialog" width="500">
      <v-card>
        <v-toolbar color="secondary" class="elevation-0">
          <v-toolbar-title>
            {{ this.item?.name }} - {{ this.item?.country }}
          </v-toolbar-title>
          <v-spacer />
          <v-btn icon @click="detailsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pt-5 px-5">
          <pre class="text-body-1">{{
            JSON.stringify(this.item?.details, null, 2)
          }}</pre>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";
import { formatDate, endOfDay } from "@/lib/utils";
import client from "@/lib/ApiClient";

const DAY_IN_MS = 24 * 60 * 60 * 1000;
const defaultStart = new Date(new Date().getTime() - 365 * DAY_IN_MS);
const defaultEnd = new Date();

export default {
  props: ["tenant_uid"],

  data: () => ({
    loading: false,
    detailsDialog: false,
    startDate: formatDate(defaultStart),
    endDate: formatDate(defaultEnd),
    search: null,
    item: null,
    headers: [
      { text: "Tenant", value: "tenant" },
      { text: "Country", value: "country" },
      { text: "Entry Type", value: "type" },
      { text: "Amount", value: "amount" },
      { text: "Quota Code", value: "quota_code" },
      { text: "Quantity", value: "quantity" },
      { text: "Created", value: "created_at" },
      { text: "Details", value: "details" },
    ],
  }),

  watch: {
    detailDialog(val) {
      if (!val) this.item = null;
    },
  },

  computed: {
    ...mapGetters(["ledger", "tenants"]),
    items() {
      return this.ledger
        ?.filter((l) => !this.tenant_uid || l.tenant_uid === this.tenant_uid)
        .filter(
          (l) =>
            new Date(l.created_at) >= new Date(this.startDate) &&
            new Date(l.created_at) < endOfDay(this.endDate)
        )
        .map((l) => {
          const tenant = this.tenants?.find((t) => t.uid === l.tenant_uid);
          return {
            tenant: tenant?.name,
            country: tenant?.shipping_country,
            type: l.entry_type,
            description: l.description,
            quota_code: l.quota_code,
            quantity: l.quantity,
            amount: `${l.amount} ${l.currency_code}`,
            created_at: new Date(l.created_at),
            details: l,
          };
        });
    },
  },

  methods: {
    showDetails(item) {
      this.item = item;
      this.detailsDialog = true;
    },

    openDetail(item) {
      this.$router.push("/instances/" + item.details.uid);
    },

    async requestCSV() {
      this.loading = true;
      const blob = await client.gateway.management.fetchLedgerCSV(
        new Date(this.startDate).toISOString(),
        endOfDay(this.endDate).toISOString()
      );

      this.loading = false;

      const url = window.URL.createObjectURL(new Blob([blob]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `NCLab Ledger Export ${this.startDate} ${this.endDate}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    },
  },
};
</script>
